import { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionsByRealtor } from '../../../services/commissionService';
import Spinner from '../../../components/common/Spinner';

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const STATUS_OPTIONS = ['전체', '완료', '상담요청', '상담', '접수', '진행', '취소'];
const FILTER_CHIPS = ['전체', '상담요청', '상담', '접수', '진행', '완료', '취소'];
const VIEW_TABS = ['일별', '월별'];

const STATUS_MAP = {
  consulting: '상담', consult: '상담',
  unread: '접수', read: '접수', assigned: '접수', received: '접수',
  reserved: '진행', visiting: '진행', in_progress: '진행',
  cancelled: '취소', canceled: '취소',
  completed: '완료', done: '완료',
};

const Stats = () => {
  const { userData } = useAuth();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [status, setStatus] = useState('전체');
  const [activeChips, setActiveChips] = useState(['전체']);
  const [viewTab, setViewTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!userData?.uid) return;
    setLoading(true);
    try {
      const srSnap = await getDocs(query(
        collection(db, 'service_requests'),
        where('source_realtor_id', '==', userData.uid)
      ));
      const srs = srSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const paSnap = await getDocs(collection(db, 'partner_assignments'));
      const paMap = {};
      paSnap.docs.forEach((d) => {
        const a = d.data();
        if (a.service_request_id) paMap[a.service_request_id] = a;
      });

      const commissions = await getCommissionsByRealtor(userData.uid);

      const list = [];
      for (const sr of srs) {
        const pa = paMap[sr.id];
        const rawStatus = pa?.status || sr.hq_status || 'received';
        const statusLabel = STATUS_MAP[rawStatus] || rawStatus;

        let cust = null;
        if (sr.customer_id) {
          try {
            const cSnap = await getDoc(doc(db, 'customers', sr.customer_id));
            if (cSnap.exists()) cust = cSnap.data();
          } catch {}
        }

        const reward = commissions.filter((c) => c.description && c.description.includes(cust?.name || '__'))
          .reduce((s, c) => s + (c.amount || 0), 0);

        const ts = sr.created_at?.toDate?.() || (sr.created_at?.seconds ? new Date(sr.created_at.seconds * 1000) : null);
        const dateStr = ts ? `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}-${String(ts.getDate()).padStart(2, '0')}` : '';
        list.push({
          id: sr.id,
          date: dateStr,
          ts: ts ? ts.getTime() : 0,
          name: cust?.name || sr.name || '-',
          status: statusLabel,
          reward,
        });
      }
      list.sort((a, b) => b.ts - a.ts);
      setRows(list);
    } catch (e) { /* skip */ }
    finally { setLoading(false); }
  }, [userData?.uid]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (!r.date) return false;
      const [y, m] = r.date.split('-').map(Number);
      if (y !== year) return false;
      if (m !== month) return false;
      if (status !== '전체' && r.status !== status) return false;
      if (!activeChips.includes('전체') && !activeChips.includes(r.status)) return false;
      return true;
    });
  }, [rows, year, month, status, activeChips]);

  if (loading) return <Spinner fullPage={false} />;

  const toggleChip = (chip) => {
    if (chip === '전체') {
      setActiveChips(['전체']);
      return;
    }
    const next = activeChips.filter((c) => c !== '전체');
    if (next.includes(chip)) {
      const filtered = next.filter((c) => c !== chip);
      setActiveChips(filtered.length ? filtered : ['전체']);
    } else {
      setActiveChips([...next, chip]);
    }
  };

  return (
    <Container>
      {/* 연도 / 월 / 상태 드롭다운 */}
      <SelectRow>
        <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {YEARS.map((y) => (
            <option key={y} value={y}>{y} 년</option>
          ))}
        </Select>
        <Select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((m) => (
            <option key={m} value={m}>{m} 월</option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
      </SelectRow>

      {/* 상태 필터 칩 */}
      <ChipRow>
        {FILTER_CHIPS.map((s) => (
          <Chip key={s} $active={activeChips.includes(s)} onClick={() => toggleChip(s)}>
            {s}
          </Chip>
        ))}
      </ChipRow>

      {/* 검색 버튼 */}
      <SearchBtn onClick={loadData}>검색</SearchBtn>

      {/* 일별 / 월별 탭 */}
      <TabRow>
        {VIEW_TABS.map((t, i) => (
          <Tab key={t} $active={viewTab === i} onClick={() => setViewTab(i)}>
            {t}
          </Tab>
        ))}
      </TabRow>

      {/* 월별 탭 안내 */}
      {viewTab === 1 && (
        <MonthlyDesc>선택한 년·월 기준으로 동일한 목록이 표시됩니다.</MonthlyDesc>
      )}

      {/* 테이블 */}
      <TableHeader>
        <ThCell>일자</ThCell>
        <ThCell>고객명</ThCell>
        <ThCell>상태</ThCell>
        <ThCell>수익금</ThCell>
      </TableHeader>

      {filtered.length === 0 ? (
        <EmptyBox>
          <EmptyIcon>!</EmptyIcon>
          <EmptyText>검색 결과가 없습니다.</EmptyText>
        </EmptyBox>
      ) : (
        filtered.map((r) => (
          <TableRow key={r.id}>
            <TdCell>{r.date}</TdCell>
            <TdCell>{r.name}</TdCell>
            <TdCell>{r.status}</TdCell>
            <TdCell>{r.reward.toLocaleString()}원</TdCell>
          </TableRow>
        ))
      )}
    </Container>
  );
};

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TdCell = styled.div`
  flex: 1;
  padding: 12px 8px;
  font-size: 13px;
  color: #1F2937;
`;

export default Stats;

// ───── 스타일 ─────
const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SelectRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Select = styled.select`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.bgWhite};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M2.5 4.5L6 8l3.5-3.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Chip = styled.button`
  padding: 8px 14px;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : '#fff'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.text};
  border: ${({ $active, theme }) => $active ? 'none' : `1px solid ${theme.colors.border}`};
`;

const SearchBtn = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md} 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.semibold : theme.fontWeight.regular};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  margin-bottom: -1px;
`;

const MonthlyDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const TableHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ThCell = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;
